import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaguesApi } from '../api/leagues';
import { Match } from '../api/matches';
import { pairsApi, Pair } from '../api/pairs';
import { useAuth } from '../contexts/AuthContext';
import LeagueStatusBadge from '../components/LeagueStatusBadge';
import GroupTable, { StandingEntry } from '../components/GroupTable';
import KoBracket from '../components/KoBracket';
import MatchCard from '../components/MatchCard';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';

type Tab = 'groups' | 'bracket' | 'matches' | 'encounters' | 'info';

function LeagueTypeBadge({ type }: { type?: string }) {
  const { t } = useTranslation();
  if (type === 'PAAR_LIGA') {
    return <span className="badge border border-green-300 bg-green-50 text-green-700 text-xs">{t('leagueList.paarLigaBadge')}</span>;
  }
  if (type === 'MANNSCHAFTS_LIGA') {
    return <span className="badge border border-orange-300 bg-orange-50 text-orange-700 text-xs">{t('leagueList.mannsLigaBadge')}</span>;
  }
  return null;
}

function PaarLigaRegister({ leagueId, isRegistered, paymentStatus, onSuccess, onError }: {
  leagueId: number;
  isRegistered?: boolean;
  paymentStatus?: string;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [selectedPairId, setSelectedPairId] = useState<number | ''>('');
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: pairs = [] } = useQuery({
    queryKey: ['my-pairs'],
    queryFn: () => pairsApi.listMine().then((r) => r.data),
  });

  const activePairs = (pairs as Pair[]).filter((p) => p.status === 'active');

  const registerMutation = useMutation({
    mutationFn: () => leaguesApi.register(leagueId, { pair_id: selectedPairId as number }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['leagues'] });
      onSuccess();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      onError(e.response?.data?.detail ?? t('leagueDetail.errorRegister'));
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: () => leaguesApi.withdraw(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['leagues'] });
      onSuccess();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      onError(e.response?.data?.detail ?? t('leagueDetail.errorDeregister'));
    },
  });

  if (isRegistered) {
    return (
      <div className="flex items-center gap-3">
        <span className="text-sm text-green-700 font-medium">
          {t('leagueDetail.paarRegistered')}
          {paymentStatus === 'pending' ? t('leagueDetail.paarPaymentPending') : ''}
          {paymentStatus === 'confirmed' ? t('leagueDetail.paarPaymentConfirmed') : ''}
        </span>
        <button
          onClick={() => withdrawMutation.mutate()}
          disabled={withdrawMutation.isPending}
          className="btn btn-danger btn-sm"
        >
          {t('leagueDetail.deregister')}
        </button>
      </div>
    );
  }

  if (activePairs.length === 0) {
    return (
      <Link to="/pairs" className="btn btn-secondary btn-sm">
        {t('leagueDetail.createPairFirst')}
      </Link>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <select
        value={selectedPairId}
        onChange={(e) => setSelectedPairId(Number(e.target.value))}
        className="input text-sm py-1.5"
      >
        <option value="">{t('leagueDetail.selectPair')}</option>
        {activePairs.map((p) => (
          <option key={p.id} value={p.id}>
            {p.player1 ? `${p.player1.first_name} ${p.player1.last_name}` : '?'} /&nbsp;
            {p.player2 ? `${p.player2.first_name} ${p.player2.last_name}` : `(${t('pairs.statusPending')})`}
          </option>
        ))}
      </select>
      <button
        onClick={() => registerMutation.mutate()}
        disabled={registerMutation.isPending || !selectedPairId}
        className="btn btn-primary btn-sm"
      >
        {registerMutation.isPending ? '...' : t('leagueDetail.register')}
      </button>
    </div>
  );
}

export default function LeagueDetail() {
  const { id } = useParams<{ id: string }>();
  const leagueId = parseInt(id!);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('groups');
  const [actionError, setActionError] = useState('');
  const { t } = useTranslation();

  const { data: league, isLoading: leagueLoading } = useQuery({
    queryKey: ['league', leagueId],
    queryFn: () => leaguesApi.get(leagueId).then((r) => r.data),
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['league-groups', leagueId],
    queryFn: () => leaguesApi.getGroups(leagueId).then((r) => r.data),
    enabled: !!league && ['active', 'knockout', 'finished'].includes(league.status),
  });

  const { data: matches = [] } = useQuery({
    queryKey: ['league-matches', leagueId],
    queryFn: () => leaguesApi.getMatches(leagueId).then((r) => r.data as Match[]),
    enabled: !!league && ['active', 'knockout', 'finished'].includes(league.status) && league.league_type !== 'MANNSCHAFTS_LIGA',
  });

  const { data: encounters = [] } = useQuery({
    queryKey: ['league-encounters', leagueId],
    queryFn: () => leaguesApi.getEncounters(leagueId).then((r) => r.data as any[]),
    enabled: !!league && ['active', 'knockout', 'finished'].includes(league.status) && league.league_type === 'MANNSCHAFTS_LIGA',
  });

  const isRoundRobin = league?.league_format === 'round_robin';

  const { data: rrStandings = [] } = useQuery({
    queryKey: ['league-standings', leagueId],
    queryFn: () => leaguesApi.getStandings(leagueId).then((r) => r.data as StandingEntry[]),
    enabled: isRoundRobin && !!league && ['active', 'finished'].includes(league.status),
  });

  const registerMutation = useMutation({
    mutationFn: () => leaguesApi.register(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['leagues'] });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? t('leagueDetail.errorRegister'));
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: () => leaguesApi.withdraw(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['leagues'] });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? t('leagueDetail.errorDeregister'));
    },
  });

  if (leagueLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-4 bg-gray-100 rounded w-2/3" />
          <div className="h-64 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (!league) {
    return <div className="text-center py-16 text-gray-500">{t('leagueDetail.notFound')}</div>;
  }

  const showGroups = ['active', 'knockout', 'finished'].includes(league.status);
  const showBracket = ['knockout', 'finished'].includes(league.status);
  const isManns = league.league_type === 'MANNSCHAFTS_LIGA';
  const isPaar = league.league_type === 'PAAR_LIGA';
  const koMatches = (matches as Match[]).filter((m) => m.phase !== 'group');
  const groupMatches = (matches as Match[]).filter((m) => m.phase === 'group');

  const tabs: { id: Tab; label: string }[] = [
    ...(showGroups && !isRoundRobin ? [{ id: 'groups' as Tab, label: t('leagueDetail.tabGroupsAndTable') }] : []),
    ...(showGroups && isRoundRobin ? [{ id: 'groups' as Tab, label: t('leagueDetail.tabTable') }] : []),
    ...(showBracket && !isRoundRobin ? [{ id: 'bracket' as Tab, label: t('leagueDetail.tabBracket') }] : []),
    ...(showGroups && isManns ? [{ id: 'encounters' as Tab, label: t('leagueDetail.tabEncounters') }] : []),
    ...(showGroups && !isManns ? [{ id: 'matches' as Tab, label: t('leagueDetail.tabMatches') }] : []),
    { id: 'info' as Tab, label: t('leagueDetail.tabInfo') },
  ];

  const currentTab = tabs.find((t) => t.id === activeTab) ? activeTab : tabs[0]?.id ?? 'info';

  function renderRegisterButton() {
    if (!user || league!.status !== 'open') return null;

    if (isPaar) {
      return (
        <div>
          {actionError && <p className="text-red-500 text-sm mb-2">{actionError}</p>}
          <PaarLigaRegister
            leagueId={leagueId}
            isRegistered={league!.is_registered}
            paymentStatus={league!.payment_status}
            onSuccess={() => setActionError('')}
            onError={setActionError}
          />
        </div>
      );
    }

    if (isManns) {
      return (
        <div className="text-sm text-gray-500 italic">
          {league!.is_registered
            ? t('leagueDetail.teamRegistered')
            : t('leagueDetail.teamCaptainRegisters')}
        </div>
      );
    }

    return (
      <div>
        {actionError && <p className="text-red-500 text-sm mb-2">{actionError}</p>}
        {league!.is_registered ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-primary-600 font-medium">
              {t('leagueDetail.isRegistered')}
              {league!.payment_status === 'pending' ? t('leagueDetail.paymentPending') : ''}
              {league!.payment_status === 'confirmed' ? t('leagueDetail.paymentConfirmed') : ''}
            </span>
            <button
              onClick={() => { setActionError(''); withdrawMutation.mutate(); }}
              disabled={withdrawMutation.isPending}
              className="btn btn-danger btn-sm"
            >
              {t('leagueDetail.deregister')}
            </button>
          </div>
        ) : (
          <button
            onClick={() => { setActionError(''); registerMutation.mutate(); }}
            disabled={registerMutation.isPending}
            className="btn btn-primary"
          >
            {registerMutation.isPending ? t('leagueDetail.registeringNow') : t('leagueDetail.registerNow')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">{league.name}</h1>
              <LeagueStatusBadge status={league.status} />
              <LeagueTypeBadge type={league.league_type} />
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
              {league.level && <span className="badge bg-gray-100 text-gray-600">{league.level}</span>}
              <span>
                {league.confirmed_count ?? 0}{' '}
                {isManns ? t('leagueList.teams') : isPaar ? t('leagueList.pairs') : t('leagueList.players')}{' '}
                {t('leagueList.confirmedSuffix')}
              </span>
              {league.fee !== undefined && league.fee !== null && (
                <span>{league.fee === 0 ? t('leagueDetail.free') : `${t('leagueDetail.fee')}: ${league.fee} €`}</span>
              )}
              {league.registration_deadline && (
                <span>{t('leagueDetail.registrationDeadline')}: {new Date(league.registration_deadline).toLocaleDateString(getDateLocale())}</span>
              )}
            </div>
          </div>

          {renderRegisterButton()}
        </div>
      </div>

      {/* Tabs */}
      {tabs.length > 1 && (
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-0 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  currentTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab content */}
      {currentTab === 'groups' && isRoundRobin && (
        <GroupTable
          groupName={t('leagueDetail.overall')}
          standings={rrStandings as StandingEntry[]}
          qualifiersPerGroup={0}
          setLabel={league.ranking_rule === 'matches' ? t('groupTable.matchesLabel') : t('groupTable.fixtureWinsLabel')}
        />
      )}

      {currentTab === 'groups' && !isRoundRobin && (
        <div className="space-y-6">
          {(groups as any[]).map((group) => (
            <div key={group.id} className="bg-primary-50 border border-primary-100 rounded-xl p-4">
              <GroupTable
                groupName={group.name}
                standings={group.standings ?? []}
                qualifiersPerGroup={league.qualifiers_per_group}
                byeCount={(group.members ?? []).filter((m: any) => m.is_bye).length}
                setLabel={isManns
                  ? (league.ranking_rule === 'matches' ? t('groupTable.matchesLabel') : t('groupTable.fixtureWinsLabel'))
                  : t('groupTable.setsLabel')}
              />
              {!isManns && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">
                    {t('leagueDetail.matchesGroup', { name: group.name })}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {groupMatches
                      .filter((m) => m.group_id === group.id && !m.is_bye)
                      .map((m) => (
                        <MatchCard key={m.id} match={m} />
                      ))}
                    {groupMatches
                      .filter((m) => m.group_id === group.id && m.is_bye)
                      .map((m) => (
                        <div key={m.id} className="card opacity-50 border-dashed border border-gray-200">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400 italic">{t('leagueDetail.bye')}</span>
                            <span className="badge bg-gray-100 text-gray-400 text-xs">{t('leagueDetail.autoWon')}</span>
                          </div>
                          <div className="mt-2 text-sm text-gray-500">
                            {isPaar
                              ? (m.home_pair
                                  ? `${m.home_pair.player1?.first_name ?? '?'} ${m.home_pair.player1?.last_name ?? ''} / ${m.home_pair.player2?.first_name ?? '?'} ${m.home_pair.player2?.last_name ?? ''}`
                                  : m.away_pair
                                  ? `${m.away_pair.player1?.first_name ?? '?'} ${m.away_pair.player1?.last_name ?? ''} / ${m.away_pair.player2?.first_name ?? '?'} ${m.away_pair.player2?.last_name ?? ''}`
                                  : '–')
                              : (m.home_player
                                  ? `${m.home_player.first_name} ${m.home_player.last_name}`
                                  : m.away_player
                                  ? `${m.away_player.first_name} ${m.away_player.last_name}`
                                  : '–')}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {currentTab === 'bracket' && (
        <div className="card">
          <KoBracket matches={koMatches} />
        </div>
      )}

      {currentTab === 'matches' && (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(matches as Match[]).filter((m) => !m.is_bye).map((m) => (
              <MatchCard key={m.id} match={m} />
            ))}
          </div>
        </div>
      )}

      {currentTab === 'encounters' && (
        <div className="space-y-3">
          {(encounters as any[]).length === 0 ? (
            <div className="text-center py-8 text-gray-500">{t('leagueDetail.noEncounters')}</div>
          ) : (
            (encounters as any[]).map((enc) => (
              <div key={enc.id} className="card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="font-medium text-gray-900">{enc.home_team?.name ?? `Team ${enc.home_team_id}`}</span>
                    <span className="text-gray-400">vs</span>
                    <span className="font-medium text-gray-900">{enc.away_team?.name ?? `Team ${enc.away_team_id}`}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {enc.home_score !== null && enc.away_score !== null && (
                      <span className="font-bold text-lg text-gray-900">
                        {enc.home_score} : {enc.away_score}
                      </span>
                    )}
                    <span className={`badge text-xs ${
                      enc.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      enc.status === 'pending' ? 'bg-gray-100 text-gray-500' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {enc.status === 'confirmed'
                        ? t('leagueDetail.encounterCompleted')
                        : enc.status === 'pending'
                        ? t('leagueDetail.encounterPending')
                        : enc.status}
                    </span>
                  </div>
                </div>
                {enc.scheduled_date && (
                  <div className="text-xs text-gray-400 mt-1">
                    {new Date(enc.scheduled_date).toLocaleString(getDateLocale())}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {currentTab === 'info' && (
        <div className="card">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {league.description && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">{t('leagueDetail.infoDescription')}</dt>
                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{league.description}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {isManns
                  ? t('leagueDetail.infoTeamsPerGroup')
                  : isPaar
                  ? t('leagueDetail.infoPairsPerGroup')
                  : t('leagueDetail.infoPlayersPerGroup')}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{league.teams_per_group}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t('leagueDetail.infoQualifiersPerGroup')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{league.qualifiers_per_group}</dd>
            </div>
            {!isManns && (
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('leagueDetail.infoMatchFormat')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{league.match_format ?? '–'}</dd>
              </div>
            )}
            {isPaar && league.pair_type && (
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('leagueDetail.infoPairType')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{league.pair_type}</dd>
              </div>
            )}
            {isManns && (
              <>
                {league.doubles_per_round && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('leagueDetail.infoDoublesPerRound')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{league.doubles_per_round}</dd>
                  </div>
                )}
                {league.rounds_per_encounter && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('leagueDetail.infoRoundsPerEncounter')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{league.rounds_per_encounter}</dd>
                  </div>
                )}
                {league.min_players_per_team && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('leagueDetail.infoMinPlayersPerTeam')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{league.min_players_per_team}</dd>
                  </div>
                )}
              </>
            )}
            {league.registration_deadline && (
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('leagueDetail.infoRegistrationDeadline')}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(league.registration_deadline).toLocaleString(getDateLocale())}
                </dd>
              </div>
            )}
            {league.end_date && (
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('leagueDetail.infoEndDate')}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(league.end_date).toLocaleDateString(getDateLocale())}
                </dd>
              </div>
            )}
            {league.activated_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('leagueDetail.infoStartedAt')}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(league.activated_at).toLocaleString(getDateLocale())}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}
